// src/pages/daily/Daily.jsx
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getWorkReports } from "../../api/const";
import { softDeleteDoc } from "../../api/papers";

import BoardTable from "../../components/common/board/BoardTable";
import BoardPagination from "../../components/common/board/BoardPagination";
import SearchBar from "../../components/common/board/SearchFilterBar";
import PageHeader from "../../components/common/ui/PageHeader";
import BoardToolbar from "../../components/common/board/BoardToolbar";

export default function Daily() {
  const { id } = useParams(); // 프로젝트 ID
  const navigate = useNavigate();

  const project = id;

  // 리스트 데이터
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  // 기본 상태
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // 검색/필터/정렬
  const [q, setQ] = useState("");
  const [searchField, setSearchField] = useState("author"); // 작성자 검색
  const [dateFilter, setDateFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchTrigger, setSearchTrigger] = useState(0);

  // 페이지네이션
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  const sumQty = (items) => {
    if (!Array.isArray(items)) return 0;
    return items.reduce((acc, item) => acc + (Number(item.qty) || 0), 0);
  };
  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = {
        project,
        [searchField]: q, // 검색 필드 동적 적용
        page,
        page_size: pageSize,
        start_date: startDate,
        end_date: endDate,
      };

      const data = await getWorkReports(params);

      // API가 total/results 구조라고 가정
      setRows(data.results ?? []);
      setTotal(data.total ?? 0);
    } catch (err) {
      console.error("리스트 불러오기 실패:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (project) fetchReports();
  }, [project, page, pageSize, searchTrigger]);

  // 검색 버튼 눌렀을 때 동작
  const applySearch = () => {
    setPage(1);
    setSearchTrigger((v) => v + 1);
  };

  // 상세 보기 이동
  const handleRowClick = (dns) => {
    navigate(`${dns}/view`);
  };

  // 삭제
  const handleDelete = async (e, docDns) => {
    e.stopPropagation();
    if (!window.confirm("이 공사일보를 삭제하시겠습니까?")) return;

    try {
      setDeleting(true);
      await softDeleteDoc(docDns, true);
      alert("공사일보가 삭제되었습니다.");
      fetchReports();
    } catch (err) {
      console.error("삭제 실패:", err);
      alert("삭제 실패");
    } finally {
      setDeleting(false);
    }
  };

  // BoardTable 컬럼 정의
  const columns = [
    {
      key: "date",
      header: "날짜",
      width: 600,
      render: (row) =>
        row.meta?.date
          ? new Date(row.meta.date).toLocaleDateString("ko-KR")
          : "-",
    },    
    {
      key: "temp",
      header: "날씨(최저/최고)",
      width: 200,
      render: (row) =>
        `${row.meta?.weather ?? "-"} (${row.meta?.temp_min ?? "-"} / ${
          row.meta?.temp_max ?? "-"
        })`,
    },
    // {
    //   key: "materials_qty",
    //   header: "자재 수량합",
    //   width: 120,
    //   render: (row) => sumQty(row.materials),
    // },
    {
      key: "equipments_qty",
      header: "장비 수량합",
      width: 100,
      render: (row) => sumQty(row.equipments),
    },
    {
      key: "personnels_qty",
      header: "인력 수량합",
      width: 100,
      render: (row) => sumQty(row.personnels),
    },
    {
      key: "created_by",
      header: "작성자",
      width: 140,
      render: (row) => row.created_by?.name ?? "-",
    },
    // {
    //   key: "confirmer",
    //   header: "확인자",
    //   width: 140,
    //   render: (row) => row.confirmer ?? "-",
    // },
    {
      key: "manage",
      // header: "관리",
      width: 40,
      render: (row) => (
        <button
          onClick={(e) => handleDelete(e, row.dns)}
          disabled={deleting}
          className="text-red-600 hover:text-red-700 underline"
        >
          {deleting ? "삭제 중..." : "삭제"}
        </button>
      ),
    },
  ];

  return (
    <div className="p-6">
      <PageHeader>공사일보 목록</PageHeader>

      <BoardToolbar
        right={
          <>
            <SearchBar
              q={q}
              onChangeQ={setQ}
              onSearch={applySearch}
              searchField={searchField}
              onChangeSearchField={setSearchField}
              dateFilter={dateFilter}
              onChangeDateFilter={setDateFilter}
              startDate={startDate}
              endDate={endDate}
              onDateChange={({ start, end }) => {
                setStartDate(start);
                setEndDate(end);
              }}
              pageSize={pageSize}
              onChangePageSize={setPageSize}
            />
            <button onClick={() => navigate("new")} className="btn-create">
              새 문서 등록
            </button>
          </>
        }
      />

      {/* 리스트 */}
      <BoardTable
        columns={columns}
        rows={rows}
        loading={loading}
        error={null}
        keyField="dns"
        onRowClick={(row) => handleRowClick(row.dns)}
      />

      {/* 페이지네이션 */}
      <BoardPagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
      />
    </div>
  );
}
